import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  api,
  formatLastSeen,
  type DeviceWithStatus,
  type OrgWithStats,
} from '../api/client'
import {
  StatusIndicator,
  NetworkQualityIndicator,
  ConnectionStateIndicator,
  LocationBadge,
  StatBadge,
} from '../components/StatusIndicator'

// Device row in detail view
function DeviceRow({ device, onClick }: { device: DeviceWithStatus, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="
        w-full flex items-center gap-4 px-4 py-3
        bg-slate-900/30 hover:bg-slate-800/50
        border-b border-slate-800/50 last:border-b-0
        transition-colors duration-150
        text-left group
      "
    >
      {/* Device status + Connection state */}
      <div className="flex items-center gap-2">
        <StatusIndicator status={device.status} pulse={device.status === 'critical'} />
        <ConnectionStateIndicator state={device.connectionState} size="sm" />
      </div>

      {/* Device info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium truncate">
            {device.userName || device.assetId || device.clientId.slice(0, 12)}
          </span>
          {device.userName && device.assetId && (
            <span className="text-slate-600 text-xs font-mono">
              {device.assetId}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-sm text-slate-500">
          <span>{device.os} {device.osVersion}</span>
          <span className="text-slate-700">|</span>
          <LocationBadge type={device.location?.type ?? 'unknown'} officeName={device.location?.officeName} compact />
        </div>
      </div>

      {/* Network quality */}
      <div className="flex items-center gap-2 min-w-[100px]">
        <NetworkQualityIndicator status={device.networkQuality?.status ?? null} size="sm" />
        {device.networkQuality ? (
          <span className="text-xs text-slate-500 font-mono">
            {device.networkQuality.latencyMs}ms
          </span>
        ) : (
          <span className="text-xs text-slate-600">--</span>
        )}
      </div>

      {/* Version and last seen */}
      <div className="text-right min-w-[80px]">
        <div className={`font-mono text-sm ${device.isCurrentVersion ? 'text-slate-400' : 'text-amber-400'}`}>
          v{device.currentVersion}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {formatLastSeen(device.lastSeen ?? device.lastCheckIn)}
        </div>
      </div>

      <svg className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  )
}

export default function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [org, setOrg] = useState<OrgWithStats | null>(null)
  const [devices, setDevices] = useState<DeviceWithStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState<string | null>(null)

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  useEffect(() => {
    if (!orgId) return

    Promise.all([
      api.getOrganization(orgId),
      api.listDevices({ orgId, sortBy: 'status' }),
    ]).then(([orgRes, devicesRes]) => {
      setOrg(orgRes.data)
      setDevices(devicesRes.data)
    }).catch(err => {
      console.error('Failed to load org detail:', err)
    }).finally(() => setLoading(false))
  }, [orgId])

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading...</div>
  }

  if (!org) {
    return <div className="p-8 text-center text-slate-500">Organization not found</div>
  }

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/orgs')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4 group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Clients
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">{org.name}</h1>
            <p className="text-slate-500 mt-1">
              {org.stats.total} device{org.stats.total !== 1 ? 's' : ''} managed
            </p>
          </div>

          <div className="flex items-center gap-6">
            <StatBadge count={org.stats.healthy} status="healthy" label="healthy" />
            <StatBadge count={org.stats.warning} status="warning" label="warning" />
            <StatBadge count={org.stats.critical} status="critical" label="critical" />
          </div>
        </div>
      </div>

      {/* Device list */}
      <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/50">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Devices</h2>
        </div>
        <div>
          {devices.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No devices found</div>
          ) : (
            devices.map(device => (
              <DeviceRow
                key={device.clientId}
                device={device}
                onClick={() => navigate(`/orgs/${orgId}/devices/${device.clientId}`)}
              />
            ))
          )}
        </div>
      </div>

      {/* Deployment Info */}
      <div className="mt-6 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/50">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Deployment Info</h2>
        </div>
        <div className="px-4 py-4 space-y-3">
          <p className="text-xs text-slate-500 mb-4">
            Add these values to your MDM configuration profile or <code className="text-slate-400">~/.nwx/config.json</code> to enroll devices.{' '}
            <a href="https://networkweather.com/docs/configuration/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
              Admin docs
            </a>
          </p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-12 shrink-0">mspId</span>
            <code className="flex-1 text-sm text-slate-300 font-mono bg-slate-800/50 px-3 py-2 rounded-lg truncate">{org.mspId}</code>
            <button
              onClick={() => copyToClipboard(org.mspId, 'mspId')}
              className="px-3 py-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors shrink-0"
            >
              {copied === 'mspId' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-12 shrink-0">orgId</span>
            <code className="flex-1 text-sm text-slate-300 font-mono bg-slate-800/50 px-3 py-2 rounded-lg truncate">{org.orgId}</code>
            <button
              onClick={() => copyToClipboard(org.orgId, 'orgId')}
              className="px-3 py-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors shrink-0"
            >
              {copied === 'orgId' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
